import type {NormalizedCacheObject} from '@apollo/client';
import {type ApolloClientProxy, EntityBaseGqlService, type ServiceResponsePayload} from '@igp/shared/api';
import type {
    Mutation,
    MutationAddReferralLinkConfigArgs,
    MutationEditReferralLinkConfigArgs,
    MutationRemoveReferralLinkConfigArgs,
    Query,
    QueryGetReferralLinkConfigsArgs,
    ReferralLinkConfig,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import type {EntityFetchRequestPayload} from '@igp/shared/redux/entity';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {ServiceTypes} from '@ioc-container';

import type {IReferralLinkConfigMutationRequestBuilder} from './requestBuilder';
import {ReferralLinkConfigRequestBuilder} from './requestBuilder';
import type {
    EditReferralLinkConfigPayload,
    ReferralLinkConfigEntityType,
    ReferralLinkConfigErrorCodes,
    ReferralLinkConfigFilterKeys,
    ReferralLinkConfigQueryFields,
    RemoveReferralLinkConfigPayload,
} from './types';

export interface IReferralLinkConfigService {
    addReferralLinkConfig(
        payload: EditReferralLinkConfigPayload
    ): Observable<ServiceResponsePayload<EditReferralLinkConfigPayload, ReferralLinkConfig>>;
    editReferralLinkConfig(
        payload: EditReferralLinkConfigPayload
    ): Observable<ServiceResponsePayload<EditReferralLinkConfigPayload, ReferralLinkConfig>>;
    removeReferralLinkConfig(
        payload: RemoveReferralLinkConfigPayload
    ): Observable<ServiceResponsePayload<RemoveReferralLinkConfigPayload, ReferralLinkConfig>>;
}

@injectable()
export class ReferralLinkConfigService
    extends EntityBaseGqlService<
        ReferralLinkConfigEntityType,
        Query,
        QueryGetReferralLinkConfigsArgs,
        ReferralLinkConfigQueryFields,
        Mutation,
        Subscription,
        ReferralLinkConfigErrorCodes,
        ReferralLinkConfigFilterKeys
    >
    implements IReferralLinkConfigService
{
    private readonly _mutationRequestBuilder: IReferralLinkConfigMutationRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer<ReferralLinkConfigErrorCodes>
    ) {
        const builder = new ReferralLinkConfigRequestBuilder();
        super(client, builder, messageLocalizer);
        this._mutationRequestBuilder = builder;
    }

    get(requestPayload: EntityFetchRequestPayload<ReferralLinkConfigEntityType, ReferralLinkConfigQueryFields>) {
        return this.fetchData(requestPayload).pipe(
            map(r => ({
                ...r,
                requestPayload,
                responsePayload: {
                    items: r.responsePayload?.getReferralLinkConfigs?.items ?? [],
                    total: r.responsePayload?.getReferralLinkConfigs?.total_count ?? 0,
                },
            }))
        );
    }

    addReferralLinkConfig(
        payload: EditReferralLinkConfigPayload
    ): Observable<ServiceResponsePayload<EditReferralLinkConfigPayload, ReferralLinkConfig>> {
        const mutation = this._mutationRequestBuilder.buildAddReferralLinkConfigMutation();
        return this._service.mutate<MutationAddReferralLinkConfigArgs>(mutation, {config: payload}).pipe(
            map(r => ({
                ...r,
                requestPayload: payload,
                responsePayload: r.responsePayload?.addReferralLinkConfig,
            }))
        );
    }

    editReferralLinkConfig(
        payload: EditReferralLinkConfigPayload
    ): Observable<ServiceResponsePayload<EditReferralLinkConfigPayload, ReferralLinkConfig>> {
        const mutation = this._mutationRequestBuilder.buildEditReferralLinkConfigMutation();
        return this._service.mutate<MutationEditReferralLinkConfigArgs>(mutation, {config: payload}).pipe(
            map(r => ({
                ...r,
                requestPayload: payload,
                responsePayload: r.responsePayload?.editReferralLinkConfig,
            }))
        );
    }

    removeReferralLinkConfig(
        payload: RemoveReferralLinkConfigPayload
    ): Observable<ServiceResponsePayload<RemoveReferralLinkConfigPayload, ReferralLinkConfig>> {
        const mutation = this._mutationRequestBuilder.buildRemoveReferralLinkConfigMutation();
        return this._service.mutate<MutationRemoveReferralLinkConfigArgs>(mutation, {config: payload}).pipe(
            map(r => ({
                ...r,
                requestPayload: payload,
                responsePayload: r.responsePayload?.removeReferralLinkConfig,
            }))
        );
    }

    protected getErrorSeverity(code: ReferralLinkConfigErrorCodes): number {
        const errorCodeSeverityMapping: Record<ReferralLinkConfigErrorCodes, number> = {
            CONFIG_ALREADY_EXIST: 1,
            INVALID_URL: 1,
            NOT_FOUND: 1,
        };
        return errorCodeSeverityMapping[code] ?? 0;
    }
}
